// Enhanced Communication Service for Supabase + Render Backend
import { shouldUseDryRun } from "../utils/environment-config";
import { shouldToastApiError } from '@/app/_module/utils/apiResponseHandler';
import { showToast } from "../utils/show-toast";
import { transformErrorResponse } from "../utils/transform-error-response";

import api from "./api";
import { getApiUrl } from "./config";
import { endpoint } from "./endpoints";
import { handleApiCall, normalizeApiError } from "../utils/apiResponseHandler";

// Enhanced Communication Types
export interface SendSmsRequest {
  to: string;
  text: string;
  fromCountry?: string;
  useUserPhone?: boolean;
  templateId?: string;
  variables?: Record<string, string>;
}

// NEW: Database-backed phone numbers response
export interface PhoneNumbersResponse {
  success: boolean;
  data: {
    states: Array<{
      id: number;
      name: string;
      code: string;
    }>;
    areaCodesByState: Record<string, number[]>;
  };
  // Standardized response fields (optional for backward compatibility)
  statusCode?: number;
  message?: string;
  timestamp?: string;
}

// NEW: Database-backed pricing response  
export interface PhoneNumberPricingResponse {
  success: boolean;
  data: {
    items: Array<{
      id: number;
      provider: string;
      area_code: string;
      price: string;
      currency: string;
      price_id: string;
      state_code: string;
      state_name: string;
      created_at: string;
      updated_at: string;
    }>;
    summary: Array<{
      areaCode: string;
      minPrice: number;
      currency: string;
    }>;
  };
  // Standardized response fields (optional for backward compatibility)
  statusCode?: number;
  message?: string;
  timestamp?: string;
}

export interface SendSmsResponse {
  statusCode: number;
  message: string;
  data: {
    messageSid?: string; // Twilio message SID
    messageId?: string; // Legacy support
    record?: any; // SMS history record
    fromNumber?: string;
    status?: string;
    to?: string;
    text?: string;
    sentAt?: string;
    cost?: number;
    segments?: number;
    a2pVerification?: {
      required: boolean;
      status: string;
      message: string;
      verificationId?: string;
    };
  };
  timestamp: string;
  timeTakenMs?: number;
  path?: string;
}

export interface SendBulkSmsRequest {
  recipients: {
    to: string;
    text: string;
    variables?: Record<string, string>;
  }[];
  fromCountry?: string;
  useUserPhone?: boolean;
  templateId?: string;
}

export interface SendBulkSmsResponse {
  statusCode: number;
  message: string;
  data: {
    batchId: string;
    totalRecipients: number;
    successCount: number;
    failureCount: number;
    messages: {
      messageId: string;
      to: string;
      status: string;
      error?: string;
    }[];
    totalCost?: number;
  };
  timestamp: string;
  timeTakenMs: number;
  path: string;
}

export interface SendEmailRequest {
  to: string;
  subject: string;
  body: string;
  cc?: string[];
  bcc?: string[];
  attachments?: {
    filename: string;
    content: string;
    contentType: string;
  }[];
  templateId?: string;
  variables?: Record<string, string>;
  // OAuth email sending (uses connected Google/Microsoft account)
  useOAuth?: boolean;
  provider?: 'google' | 'microsoft'; // Optional: specify which OAuth provider to use
  leadId?: string; // Associated lead ID for email history
}

export interface SendEmailResponse {
  statusCode: number;
  message: string;
  data: {
    emailId: string;
    status: string;
    to: string;
    subject: string;
    body: string;
    sentAt: string;
    deliveryStatus?: string;
  };
  timestamp: string;
  timeTakenMs: number;
  path: string;
}

export interface AreaCode {
  code: string;
  region: string;
  isSelected: boolean;
  cost?: number;
  availability?: 'available' | 'limited' | 'unavailable';
}

export interface StateAreaCode {
  state: string;
  abbreviation: string;
  areaCodes: AreaCode[];
}

export interface CheckAreaNumberAvailableParams {
  areaCodes: number[] | string[];
  state?: string;
}

export interface CheckAreaNumberAvailableResponse {
  statusCode: number;
  message: string;
  data: {
    available: number[];
    unavailable: number[];
    costs: Record<string, number>;
  };
  timestamp: string;
  timeTakenMs: number;
  path: string;
}

export interface StartAreaNumberCheckoutParams {
  areaCodes: number[];
  state: string;
  amount: number;
  quantity: number;
  provider?: string;
  currency?: string;
}

export interface StartAreaNumberCheckoutResponse {
  statusCode: number;
  message: string;
  data: {
    url: string;
    transactionId: string;
    dryRun: boolean;
    expiresAt: string;
  };
  timestamp: string;
  timeTakenMs: number;
  path: string;
}

export interface SubscribeToAreaCodesParams {
  areaCodes: number[];
  state: string;
  transactionId: string;
}

export interface SubscribeToAreaCodesResponse {
  statusCode: number;
  message: string;
  data: {
    provisionedNumbers: {
      statusCode: number;
      success: boolean;
      purchasedNumbers: {
        areaCode: number;
        phoneNumber: string;
        phoneSid: string;
        dryRun: boolean;
      }[];
      errors: string[];
      message: string;
      dryRun: boolean;
    };
  };
  timestamp: string;
  timeTakenMs: number;
  path: string;
}

export interface AreaCodeCostResponse {
  statusCode: number;
  message: string;
  data: {
    areaCodes: number[];
    cost: number;
    currency: string;
    breakdown: Record<string, number>;
  };
  timestamp: string;
  timeTakenMs: number;
  path: string;
}

// Enhanced Communication Service Class
class EnhancedCommunicationService {
  // Validate phone number format
  private validatePhoneNumber(phoneNumber: string): boolean {
    // Basic phone number validation (can be enhanced)
    const phoneRegex = /^\+?[1-9]\d{1,14}$/;
    return phoneRegex.test(phoneNumber.replace(/[\s\-\(\)]/g, ''));
  }

  // Get voicemail data (hybrid: demo vs real)
  // Note: Voicemails are stored as call_histories with specific outcomes in the database
  async getVoicemails(): Promise<{ items: any[] }> {
    try {

      // Fetch voicemails from call_histories table filtered by outcome='voicemail'
      const { endpoint } = await import("./endpoints");
      const response = await api.get<{ items?: any[] } | any>({ 
        url: `${getApiUrl(endpoint.communication.getCalls)}?outcome=voicemail` 
      });
      
      const dataUnknown = response as unknown as Record<string, unknown>;
      const items = Array.isArray((dataUnknown.items as unknown)) ? (dataUnknown.items as any[]) : (Array.isArray(response.data) ? response.data : []);
      return { items };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      
      // Handle 404 as empty result instead of logging warning for voicemails
      if (errorMessage?.includes('404')) {
        return { items: [] };
      }

      return { items: [] };
    }
  }

  // Validate email format
  private validateEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  // Handle communication errors
  private handleError(error: unknown, operation: string): never {
    const normalized = normalizeApiError(error);
    const message = normalized.message || `Failed to ${operation}`;
    const retrySuffix = typeof normalized.retryAfterSeconds === 'number'
      ? ` (retry after ${normalized.retryAfterSeconds}s)`
      : '';
    // silent = MFA-window 403 the interceptor self-recovers — never toast it
    if (shouldToastApiError(normalized, operation)) { showToast(`${message}${retrySuffix}`, "error") }

    const e = new Error(message) as Error & { code?: string | number; retryAfterSeconds?: number };
    e.code = normalized.code;
    e.retryAfterSeconds = normalized.retryAfterSeconds;
    throw e;
  }

  // Send SMS
  async sendSms(data: SendSmsRequest): Promise<SendSmsResponse> {
    try {
      // Validate input data
      if (!data.to?.trim()) {
        throw new Error('Recipient phone number is required');
      }
      if (!data.text?.trim()) {
        throw new Error('SMS message text is required');
      }
      if (!this.validatePhoneNumber(data.to)) {
        throw new Error('Invalid phone number format');
      }
      if (data.text.length > 1600) {
        throw new Error('SMS message is too long (max 1600 characters)');
      }

      // Sanitize data
      const sanitizedData = {
        ...data,
        to: data.to.trim(),
        text: data.text.trim(),
        fromCountry: data.fromCountry?.trim() || undefined,
      };

      const url = getApiUrl(endpoint.communication.sendSms);

      const response = await api.post<SendSmsResponse>({
        url,
        body: sanitizedData,
        auth: true,
      });

      if (!response.data) {
        throw new Error('Invalid response from SMS service');
      }

      showToast("SMS sent successfully", "success");
      return response.data;
    } catch (error) {
      this.handleError(error, 'send SMS');
    }
  }

  // Send Bulk SMS
  async sendBulkSms(data: SendBulkSmsRequest): Promise<SendBulkSmsResponse> {
    try {
      // Validate input data
      if (!data.recipients?.length) {
        throw new Error('At least one recipient is required');
      }
      if (data.recipients.length > 1000) {
        throw new Error('Too many recipients (max 1000 per batch)');
      }

      // Validate each recipient
      for (const recipient of data.recipients) {
        if (!recipient.to?.trim()) {
          throw new Error('All recipients must have phone numbers');
        }
        if (!recipient.text?.trim()) {
          throw new Error('All recipients must have message text');
        }
        if (!this.validatePhoneNumber(recipient.to)) {
          throw new Error(`Invalid phone number format: ${recipient.to}`);
        }
        if (recipient.text.length > 1600) {
          throw new Error(`Message too long for ${recipient.to} (max 1600 characters)`);
        }
      }

      // Sanitize data
      const sanitizedData = {
        ...data,
        recipients: (data.recipients ?? []).map(recipient => ({
          ...recipient,
          to: recipient.to.trim(),
          text: recipient.text.trim(),
        })),
        fromCountry: data.fromCountry?.trim() || undefined,
      };

      let url = getApiUrl(endpoint.communication.sendBulkSms);
      if (shouldUseDryRun()) {
        url += "?dryRun=true";
      }

      const response = await api.post<SendBulkSmsResponse>({
        url,
        body: sanitizedData,
        auth: true,
      });

      if (!response.data) {
        throw new Error('Invalid response from bulk SMS service');
      }

      showToast(`Bulk SMS sent to ${data.recipients.length} recipients`, "success");
      return response.data;
    } catch (error) {
      this.handleError(error, 'send bulk SMS');
    }
  }

  // Send Email (OAuth or SendGrid)
  async sendEmail(data: SendEmailRequest): Promise<SendEmailResponse> {
    try {
      // Validate input data
      if (!data.to?.trim()) {
        throw new Error('Recipient email address is required');
      }
      if (!data.subject?.trim()) {
        throw new Error('Email subject is required');
      }
      if (!data.body?.trim()) {
        throw new Error('Email body is required');
      }
      if (!this.validateEmail(data.to)) {
        throw new Error('Invalid email address format');
      }

      // Validate CC and BCC emails if provided
      if (data.cc) {
        for (const email of data.cc) {
          if (!this.validateEmail(email)) {
            throw new Error(`Invalid CC email address: ${email}`);
          }
        }
      }
      if (data.bcc) {
        for (const email of data.bcc) {
          if (!this.validateEmail(email)) {
            throw new Error(`Invalid BCC email address: ${email}`);
          }
        }
      }

      // Sanitize data
      const sanitizedData = {
        ...data,
        to: data.to.trim().toLowerCase(),
        subject: data.subject.trim(),
        body: data.body.trim(),
        cc: data.cc?.map(email => email.trim().toLowerCase()),
        bcc: data.bcc?.map(email => email.trim().toLowerCase()),
      };

      // Determine which endpoint to use based on OAuth preference
      let url: string;
      if (data.useOAuth) {
        // Use OAuth email endpoint (sends via connected Google/Microsoft account)
        url = getApiUrl(endpoint.communication.sendEmailViaOAuth);
      } else {
        // Use regular SendGrid endpoint
        url = getApiUrl(endpoint.communication.sendEmail);
      }

      if (shouldUseDryRun()) {
        url += "?dryRun=true";
      }

      const response = await api.post<SendEmailResponse>({
        url,
        body: sanitizedData,
        auth: true,
      });

      if (!response.data) {
        throw new Error('Invalid response from email service');
      }

      const providerName = data.useOAuth
        ? (data.provider === 'microsoft' ? 'Microsoft' : 'Gmail')
        : 'SendGrid';

      showToast(`Email sent successfully via ${providerName}`, "success");
      return response.data;
    } catch (error) {
      this.handleError(error, 'send email');
    }
  }

  // Download Email Attachment from Gmail/Outlook
  async downloadEmailAttachment(
    messageId: string,
    attachmentId: string
  ): Promise<{
    filename: string;
    contentType: string;
    size: number;
    content: string; // Base64 encoded content
  }> {
    try {
      const url = getApiUrl(endpoint.communication.downloadEmailAttachment(messageId, attachmentId));

      const response = await api.get<{
        statusCode: number;
        message: string;
        data: {
          filename: string;
          contentType: string;
          size: number;
          content: string;
        };
      }>({
        url,
        auth: true,
      });

      // Note: axios interceptor unwraps response.data, so response IS the backend response body
      const responseData = response as unknown as {
        statusCode: number;
        message: string;
        data: {
          filename: string;
          contentType: string;
          size: number;
          content: string;
        };
      };

      if (!responseData?.data) {
        throw new Error('Invalid response from attachment download');
      }

      return responseData.data;
    } catch (error) {
      this.handleError(error, 'download email attachment');
      throw error;
    }
  }

  // Get fresh attachment info from Gmail/Outlook (with IDs for downloading)
  async getEmailAttachments(messageId: string): Promise<{
    attachments: Array<{
      id: string;
      filename: string;
      contentType: string;
      size: number;
    }>;
  }> {
    try {
      const url = getApiUrl(endpoint.communication.getEmailAttachments(messageId));

      const response = await api.get<{
        statusCode: number;
        message: string;
        data: {
          attachments: Array<{
            id: string;
            filename: string;
            contentType: string;
            size: number;
          }>;
        };
      }>({
        url,
        auth: true,
      });

      // Note: axios interceptor unwraps response.data, so response IS the backend response body
      const responseData = response as unknown as {
        statusCode: number;
        message: string;
        data: {
          attachments: Array<{
            id: string;
            filename: string;
            contentType: string;
            size: number;
          }>;
        };
      };

      if (!responseData?.data) {
        throw new Error('Invalid response from get email attachments');
      }

      return responseData.data;
    } catch (error) {
      // Don't show toast for this - it's a background operation
      console.error('Failed to get email attachments:', error);
      throw error;
    }
  }

  // Get Area Codes (NEW: Database-backed endpoint)
  async getAreaCodes(): Promise<Record<string, number[]>> {
    try {
      const result = await handleApiCall<PhoneNumbersResponse>(async () => {
        const response = await api.get<PhoneNumbersResponse>({
          url: getApiUrl(endpoint.communication.getPhoneNumbers),
          auth: true, // Ensure authentication header is included
        });
        return response;
      });

      // Handle different response structures with better error handling
      if ((result as any)?.data?.areaCodesByState) {
        return (result as any).data.areaCodesByState;
      } else if ((result as any)?.areaCodesByState) {
        return (result as any).areaCodesByState;
      } else if (result && typeof result === 'object') {
        // Check if it's already in the expected format
        if ((result as any).areaCodesByState) {
          return (result as any).areaCodesByState;
        }
        // If result is an object but doesn't have expected structure, check if it's a direct mapping
        if (Object.keys(result).length > 0 && Object.values(result).every(v => Array.isArray(v))) {
          return result as unknown as Record<string, number[]>;
        }
      }
      
      // Handle empty array response - this is valid, just means no area codes available
      if (Array.isArray(result) && result.length === 0) {
        return {};
      }
      
      // Handle null or undefined result
      if (!result) {
        return {};
      }
      
      // Log the unexpected response structure for debugging
      // Instead of throwing an error, return empty object to avoid breaking stats
      return {};
    } catch (error) {
      // Fallback to legacy endpoint for backward compatibility
      try {
        const legacyResult = await handleApiCall<Record<string, number[]>>(async () => {
          const response = await api.get<{
            statusCode: number;
            message: string;
            data: Record<string, number[]>;
            timestamp: string;
            timeTakenMs: number;
          }>({
            url: getApiUrl(endpoint.communication.getCommunication),
            auth: true, // Ensure authentication header is included
          });
          return response;
        });

        return legacyResult;
      } catch (legacyError) {
        // Even if both endpoints fail, don't throw an error that breaks the stats
        // Just return an empty object so the stats can still be calculated
        return {};
      }
    }
  }

  // NEW: Get phone numbers with states filter
  async getPhoneNumbers(stateCode?: string, requireAuth: boolean = true): Promise<PhoneNumbersResponse> {
    try {
      // Use public endpoint when auth not required (signup flow)
      const baseEndpoint = requireAuth
        ? endpoint.communication.getPhoneNumbers
        : endpoint.communication.getPhoneNumbersPublic;

      const url = stateCode
        ? `${getApiUrl(baseEndpoint)}?state=${encodeURIComponent(stateCode)}`
        : getApiUrl(baseEndpoint);

      const response = await api.get<PhoneNumbersResponse>({
        url,
        auth: requireAuth,
      });

      // Standardized response handling - expect PhoneNumbersResponse format
      if (response.data?.success !== undefined) {
        // Direct PhoneNumbersResponse format
        return response.data;
      }

      // Handle standardized API response format
      if (response.data?.statusCode !== undefined) {
        if (response.data.statusCode === 200 || response.data.statusCode === 201) {
          // Transform standardized format to PhoneNumbersResponse format
          return {
            success: true,
            data: response.data.data,
            statusCode: response.data.statusCode,
            message: response.data.message,
            timestamp: response.data.timestamp
          };
        } else {
          throw new Error(`Phone numbers service error: ${response.data.message || 'Unknown error'}`);
        }
      }

      throw new Error('Invalid response from phone numbers service');
    } catch (error) {
      this.handleError(error, 'fetch phone numbers');
    }
  }

  // NEW: Get area codes for signup (public endpoint, no auth required)
  async getAreaCodesForSignup(): Promise<PhoneNumbersResponse> {
    try {
      const url = getApiUrl(endpoint.communication.getAreaCodesPublic);

      const response = await api.get<any>({
        url,
        auth: false, // No authentication required for signup
      });

      // The axios interceptor returns response.data, so what we get here IS the backend response
      // Type it as any to access the backend response properties
      const backendResponse: any = response;

      // Check if we got a valid response
      if (!backendResponse) {
        throw new Error('No response from area codes service');
      }

      // Handle standardized API response format (statusCode, data, message)
      if (backendResponse.statusCode !== undefined && backendResponse.data !== undefined) {
        if (backendResponse.statusCode === 200 || backendResponse.statusCode === 201) {
          // Transform standardized format to PhoneNumbersResponse format
          return {
            success: true,
            data: backendResponse.data,
            statusCode: backendResponse.statusCode,
            message: backendResponse.message,
            timestamp: backendResponse.timestamp
          };
        } else {
          throw new Error(`Area codes service error: ${backendResponse.message || 'Unknown error'}`);
        }
      }

      // Direct PhoneNumbersResponse format (success, data)
      if (backendResponse.success !== undefined && backendResponse.data !== undefined) {
        return backendResponse;
      }

      throw new Error('Invalid response from area codes service');
    } catch (error) {
      this.handleError(error, 'fetch area codes for signup');
    }
  }

  // NEW: Get phone number pricing
  async getPhoneNumberPricing(stateCode?: string, areaCode?: string, requireAuth: boolean = true): Promise<PhoneNumberPricingResponse> {
    try {
      // Use public endpoint when auth not required (signup flow)
      const baseEndpoint = requireAuth
        ? endpoint.communication.getPhoneNumbersPricing
        : endpoint.communication.getPhoneNumbersPricingPublic;

      const params = new URLSearchParams();
      if (stateCode) params.append('state', stateCode);
      if (areaCode) params.append('areaCode', areaCode);

      const url = params.toString()
        ? `${getApiUrl(baseEndpoint)}?${params.toString()}`
        : getApiUrl(baseEndpoint);

      const response = await api.get<PhoneNumberPricingResponse>({
        url,
        auth: requireAuth,
      });

      // The axios interceptor returns response.data, so what we get here IS the backend response
      // Type it as any to access the backend response properties
      const backendResponse: any = response;

      // Check if we got a valid response
      if (!backendResponse) {
        throw new Error('No response from phone number pricing service');
      }

      // Handle standardized API response format (statusCode, data, message)
      if (backendResponse.statusCode !== undefined && backendResponse.data !== undefined) {
        if (backendResponse.statusCode === 200 || backendResponse.statusCode === 201) {
          // Transform standardized format to PhoneNumberPricingResponse format
          return {
            success: true,
            data: backendResponse.data
          };
        } else {
          throw new Error(`Phone number pricing service error: ${backendResponse.message || 'Unknown error'}`);
        }
      }

      // Direct PhoneNumberPricingResponse format (success, data)
      if (backendResponse.success !== undefined && backendResponse.data !== undefined) {
        return backendResponse;
      }

      throw new Error('Invalid response from phone number pricing service');
    } catch (error) {
      this.handleError(error, 'fetch phone number pricing');
    }
  }

  // Check Area Number Availability
  async checkAreaNumberAvailable(params: CheckAreaNumberAvailableParams): Promise<CheckAreaNumberAvailableResponse> {
    try {
      // Validate input
      if (!params.areaCodes?.length) {
        throw new Error('At least one area code is required');
      }
      if (params.areaCodes.length > 50) {
        throw new Error('Too many area codes (max 50 per request)');
      }

      // Validate area codes format
      for (const raw of params.areaCodes) {
        const codeNum = Number(raw);
        if (!Number.isInteger(codeNum) || codeNum < 200 || codeNum > 999) {
          throw new Error(`Invalid area code: ${raw}`);
        }
      }

      const dryRun = shouldUseDryRun();
      const url = `${getApiUrl(endpoint.communication.checkAreaNumberAvailable)}?dryRun=${dryRun}`;
      
      const response = await api.post<CheckAreaNumberAvailableResponse>({
        url,
        body: {
          ...params,
          // Backend accepts strings; normalize to strings to be safe
          areaCodes: params.areaCodes.map((c) => String(c)),
        },
        auth: true,
      });

      if (!response.data) {
        throw new Error('Invalid response from area number availability service');
      }

      return response.data;
    } catch (error) {
      this.handleError(error, 'check area number availability');
    }
  }

  // Get Area Code Cost
  async getAreaCodeCost(codes: string, stateName?: string): Promise<AreaCodeCostResponse> {
    try {
      if (!codes?.trim()) {
        throw new Error('Area codes parameter is required');
      }

      const url = `${getApiUrl(endpoint.communication.getAreaCodeCost)}?codes=${encodeURIComponent(codes)}${
        stateName ? `&stateName=${encodeURIComponent(stateName)}` : ''
      }`;
      
      const response = await api.get<AreaCodeCostResponse>({
        url,
        auth: true,
      });

      if (!response.data) {
        throw new Error('Invalid response from area code cost service');
      }

      return response.data;
    } catch (error) {
      this.handleError(error, 'fetch area code cost');
    }
  }

  // Start Area Number Checkout
  async startAreaNumberCheckout(params: StartAreaNumberCheckoutParams): Promise<StartAreaNumberCheckoutResponse> {
    try {
      // Validate input
      if (!params.areaCodes?.length) {
        throw new Error('At least one area code is required');
      }
      if (!params.state?.trim()) {
        throw new Error('State is required');
      }
      if (!params.amount || params.amount <= 0) {
        throw new Error('Valid amount is required');
      }
      if (!params.quantity || params.quantity <= 0) {
        throw new Error('Valid quantity is required');
      }

      // Validate area codes
      for (const code of params.areaCodes) {
        if (!Number.isInteger(code) || code < 200 || code > 999) {
          throw new Error(`Invalid area code: ${code}`);
        }
      }

      const sanitizedParams = {
        // Backend accepts strings; normalize for consistency
        areaCodes: params.areaCodes.map((c) => String(c)),
        state: params.state.trim(),
        amount: params.amount,
        quantity: params.quantity,
        provider: params.provider || 'stripe',
        currency: params.currency || 'usd',
      };

      const url = `${getApiUrl(endpoint.communication.startAreaNumberCheckout)}?dryRun=${shouldUseDryRun()}`;
      
      const response = await api.post<StartAreaNumberCheckoutResponse>({
        url,
        body: sanitizedParams,
        auth: true,
      });

      if (!response.data) {
        throw new Error('Invalid response from area number checkout service');
      }

      return response.data;
    } catch (error) {
      this.handleError(error, 'start area number checkout');
    }
  }

  // Subscribe to Area Codes
  async subscribeToAreaCodes(params: SubscribeToAreaCodesParams): Promise<SubscribeToAreaCodesResponse> {
    try {
      // Validate input
      if (!params.areaCodes?.length) {
        throw new Error('At least one area code is required');
      }
      if (!params.state?.trim()) {
        throw new Error('State is required');
      }
      if (!params.transactionId?.trim()) {
        throw new Error('Transaction ID is required');
      }

      // Validate area codes
      for (const code of params.areaCodes) {
        if (!Number.isInteger(code) || code < 200 || code > 999) {
          throw new Error(`Invalid area code: ${code}`);
        }
      }

      const sanitizedParams = {
        // Backend accepts strings; normalize
        areaCodes: params.areaCodes.map((c) => String(c)),
        state: params.state.trim(),
        transactionId: params.transactionId.trim(),
      };

      const url = `${getApiUrl(endpoint.communication.subscribeToAreaCodes)}?dryRun=${shouldUseDryRun()}`;
      
      const response = await api.post<SubscribeToAreaCodesResponse>({
        url,
        body: sanitizedParams,
        auth: true,
      });

      if (!response.data) {
        throw new Error('Invalid response from area codes subscription service');
      }

      const successCount = response.data.data.provisionedNumbers.purchasedNumbers?.length || 0;
      showToast(`Successfully subscribed to ${successCount} area codes`, "success");

      return response.data;
    } catch (error) {
      this.handleError(error, 'subscribe to area codes');
    }
  }

  // Batch operations for SMS
  async sendSmsToMultipleRecipients(
    recipients: string[], 
    message: string, 
    options?: Partial<SendSmsRequest>
  ): Promise<SendBulkSmsResponse> {
    try {
      if (!recipients.length) {
        throw new Error('At least one recipient is required');
      }

      const bulkRequest: SendBulkSmsRequest = {
        recipients: recipients.map(to => ({
          to,
          text: message,
          variables: options?.variables,
        })),
        fromCountry: options?.fromCountry,
        useUserPhone: options?.useUserPhone,
        templateId: options?.templateId,
      };

      return await this.sendBulkSms(bulkRequest);
    } catch (error) {
      this.handleError(error, 'send SMS to multiple recipients');
    }
  }

  // Get calls data
  async getCalls(params: any = {}): Promise<{ items: any[] }> {
    try {
      const response = await api.get<{
        statusCode: number;
        message: string;
        data: { items: any[] };
        timestamp: string;
        timeTakenMs: number;
      }>({
        url: getApiUrl(endpoint.communication.getCalls),
        auth: true, // Ensure authentication header is included
      });

      return response.data?.data || { items: [] };
    } catch (error) {

      return { items: [] };
    }
  }

  // Get SMS data
  async getSms(params: any = {}): Promise<{ items: any[] }> {
    try {
      const response = await api.get<{
        statusCode: number;
        message: string;
        data: { items: any[] };
        timestamp: string;
        timeTakenMs: number;
      }>({
        url: getApiUrl(endpoint.communication.getSms || "/communication/sms"),
        auth: true, // Ensure authentication header is included
      });

      return response.data?.data || { items: [] };
    } catch (error) {

      return { items: [] };
    }
  }

  // Get Email history data
  async getEmails(params?: {
    leadId?: string;
    status?: string;
    type?: string;
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<{ items: any[]; meta?: any }> {
    try {
      const queryParams = new URLSearchParams();

      if (params?.leadId) queryParams.append('leadId', params.leadId);
      if (params?.status) queryParams.append('status', params.status);
      if (params?.type) queryParams.append('type', params.type);
      if (params?.search) queryParams.append('search', params.search);
      if (params?.page) queryParams.append('page', params.page.toString());
      if (params?.limit) queryParams.append('limit', params.limit.toString());

      const url = `${getApiUrl(endpoint.communication.getEmails)}?${queryParams.toString()}`;

      const response = await api.get<{
        statusCode: number;
        message: string;
        success: boolean;
        data: {
          items: any[];
          meta: {
            totalItems: number;
            page: number;
            totalPages: number;
            itemCount: number;
            limit: number;
          };
        };
        timestamp: string;
      }>({
        url,
        auth: true, // Ensure authentication header is included
      });

      return response.data?.data || { items: [], meta: {} };
    } catch (error) {

      return { items: [], meta: {} };
    }
  }

  // Mark email as opened (manual tracking from CRM)
  async markEmailOpened(emailId: string): Promise<boolean> {
    try {
      const url = getApiUrl(endpoint.communication.markEmailOpened(emailId));

      const response = await api.post<{
        statusCode: number;
        message: string;
        success: boolean;
        data: any;
        timestamp: string;
      }>({
        url,
        auth: true,
      });

      return response.data?.success || false;
    } catch (error) {
      console.error('Error marking email as opened:', error);
      return false;
    }
  }

  // Create Bridge Call (phone-based calling)
  async createBridgeCall(params: {
    to: string;
    from?: string;
    leadId?: string;
    leadName?: string;
  }): Promise<{ callSid: string; status: string; message: string }> {
    try {
      // Validate input data
      if (!params.to?.trim()) {
        throw new Error('Recipient phone number is required');
      }
      if (!this.validatePhoneNumber(params.to)) {
        throw new Error('Invalid phone number format');
      }

      const response = await api.post<{
        statusCode: number;
        message: string;
        data: {
          callSid: string;
          status: string;
        } | null;
        timestamp: string;
      }>({
        url: getApiUrl('/api/communication/bridge-call'),
        body: params,
        auth: true,
      });

      if (!response.data) {
        throw new Error('Invalid response from bridge call service');
      }

      const responseData = response.data as any;

      // Check if the request failed (backend returns error with data: null)
      if (!responseData.data || responseData.statusCode !== 200) {
        throw new Error(responseData.message || 'Failed to create bridge call');
      }

      const data = responseData.data;

      return {
        callSid: data.callSid,
        status: data.status,
        message: responseData.message || 'Bridge call initiated',
      };
    } catch (error) {
      this.handleError(error, 'create bridge call');
    }
  }

  // Get communication statistics
  async getCommunicationStats(range?: { startDate?: string; endDate?: string }): Promise<{
    totalCalls: number;
    totalTime: number;
    avgCallDuration: number;
    answeredCalls: number;
    totalSMS: number;
    callAnswerRate: number;
    smsCount: number;
    emailCount: number;
    areaCodesCount: number;
    totalCost: number;
    faxCount: number;
    weeklyTotalCalls: number;
    weeklyAnsweredCalls: number;
    weeklyAnswerRate: number;
  }> {
    try {
      const qs = new URLSearchParams();
      if (range?.startDate) qs.set('startDate', range.startDate);
      if (range?.endDate) qs.set('endDate', range.endDate);
      const url = `${getApiUrl(endpoint.communication.getStats)}${qs.toString() ? `?${qs.toString()}` : ''}`;

      // Use the new dedicated stats endpoint from backend
      const response = await api.get<{
        statusCode: number;
        message: string;
        data: {
          totalCalls: number;
          answeredCalls: number;
          totalTime: number;
          avgCallDuration: number;
          totalSMS: number;
          callAnswerRate: number;
          smsCount: number;
          emailCount: number;
          faxCount: number;
        };
        timestamp: string;
      }>({
        url,
        auth: true,
      });

      // Axios wraps the backend response in response.data
      // Backend returns: { statusCode, message, data: { totalCalls, ... } }
      // Axios makes it: { data: { statusCode, message, data: { totalCalls, ... } } }
      // So we need response.data.data to get the actual stats
      const backendResponse = (response as any).data;
      const stats = backendResponse?.data || backendResponse;

      const result = {
        totalCalls: stats?.totalCalls || 0,
        totalTime: stats?.totalTime || 0,
        avgCallDuration: stats?.avgCallDuration || 0,
        answeredCalls: stats?.answeredCalls || 0,
        totalSMS: stats?.totalSMS || 0,
        callAnswerRate: stats?.callAnswerRate || 0,
        smsCount: stats?.smsCount || 0,
        emailCount: stats?.emailCount || 0,
        areaCodesCount: 0, // Not included in stats endpoint
        totalCost: 0, // Not included in stats endpoint
        faxCount: stats?.faxCount || 0,
        weeklyTotalCalls: stats?.weeklyTotalCalls || 0,
        weeklyAnsweredCalls: stats?.weeklyAnsweredCalls || 0,
        weeklyAnswerRate: stats?.weeklyAnswerRate || 0,
      };

      return result;
    } catch (error) {
      // Log error for debugging
      console.error('[CommunicationService] getCommunicationStats error:', error);

      // Fallback to zero values if API fails - this prevents dashboard from breaking
      // The error is logged above for debugging purposes
      return {
        totalCalls: 0,
        totalTime: 0,
        avgCallDuration: 0,
        answeredCalls: 0,
        totalSMS: 0,
        callAnswerRate: 0,
        smsCount: 0,
        emailCount: 0,
        areaCodesCount: 0,
        totalCost: 0,
        faxCount: 0,
        weeklyTotalCalls: 0,
        weeklyAnsweredCalls: 0,
        weeklyAnswerRate: 0,
      };
    }
  }

  // Outbound-call breakdown by dialer: manual / power dialer / multi-line.
  async getDialerBreakdown(range?: { startDate?: string; endDate?: string }): Promise<{
    manual: number;
    powerDialer: number;
    multiLine: number;
    total: number;
  }> {
    try {
      const qs = new URLSearchParams();
      if (range?.startDate) qs.set('startDate', range.startDate);
      if (range?.endDate) qs.set('endDate', range.endDate);
      const url = `${getApiUrl(endpoint.communication.getDialerBreakdown)}${qs.toString() ? `?${qs.toString()}` : ''}`;
      const response = await api.get<any>({ url, auth: true });
      const body = (response as any).data;
      const d = body?.data || body || {};
      return {
        manual: Number(d.manual) || 0,
        powerDialer: Number(d.powerDialer) || 0,
        multiLine: Number(d.multiLine) || 0,
        total: Number(d.total) || 0,
      };
    } catch (error) {
      console.error('[CommunicationService] getDialerBreakdown error:', error);
      return { manual: 0, powerDialer: 0, multiLine: 0, total: 0 };
    }
  }

  // ============================================
  // VOICEMAIL DROP LIBRARY MANAGEMENT
  // ============================================

  // Get all voicemail drops for the current user
  async getVoicemailDrops(): Promise<{ data: any[] }> {
    try {
      const url = getApiUrl('/api/communication/voicemail-drops');
      const response = await api.get<{
        statusCode: number;
        message: string;
        data: any[];
        timestamp: string;
      }>({
        url,
        auth: true,
      });

      // Axios interceptor already unwraps response.data, so `response` IS the backend body
      // Backend returns { statusCode, message, data: [...drops] }
      const backendResponse = response as any;
      return { data: backendResponse?.data || [] };
    } catch (error) {
      showToast('Failed to load voicemail drops', 'error');
      return { data: [] };
    }
  }

  // Upload voicemail drop audio file
  async uploadVoicemailDropAudio(audioFile: File | Blob): Promise<{ audioUrl: string; filename: string; size: number }> {
    try {
      const url = getApiUrl('/api/communication/voicemail-drops/upload');

      const formData = new FormData();
      formData.append('audio', audioFile, audioFile instanceof File ? audioFile.name : 'voicemail.wav');

      // Use native fetch for FormData upload
      const session = await import('./auth').then(m => m.getSupabaseSession());
      const accessToken = session?.access_token;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to upload audio file');
      }

      const data = await response.json();
      if (data.statusCode !== 200 || !data.data?.audioUrl) {
        throw new Error(data.message || 'Failed to upload audio file');
      }

      return data.data;
    } catch (error) {
      showToast('Failed to upload audio file', 'error');
      throw error;
    }
  }

  // Create a new voicemail drop (after uploading audio)
  async createVoicemailDrop(data: {
    name: string;
    audioUrl: string;
    duration?: number;
    fileSize?: number;
    description?: string;
    isDefault?: boolean;
  }): Promise<any> {
    try {
      const url = getApiUrl('/api/communication/voicemail-drops');

      const response = await api.post<{
        statusCode: number;
        message: string;
        data: any;
        timestamp: string;
      }>({
        url,
        body: data,
        auth: true,
      });

      return response.data;
    } catch (error) {
      showToast('Failed to create voicemail drop', 'error');
      throw error;
    }
  }

  // Set a voicemail drop as default
  async setDefaultVoicemailDrop(id: string): Promise<any> {
    try {
      const url = getApiUrl(`/api/communication/voicemail-drops/${id}/set-default`);
      const response = await api.post<{
        statusCode: number;
        message: string;
        data: any;
        timestamp: string;
      }>({
        url,
        auth: true,
      });

      return response.data;
    } catch (error) {
      showToast('Failed to set default voicemail drop', 'error');
      throw error;
    }
  }

  // Delete a voicemail drop
  async deleteVoicemailDrop(id: string): Promise<any> {
    try {
      const url = getApiUrl(`/api/communication/voicemail-drops/${id}`);
      const response = await api.delete<{
        statusCode: number;
        message: string;
        timestamp: string;
      }>({
        url,
        auth: true,
      });

      return response.data;
    } catch (error) {
      showToast('Failed to delete voicemail drop', 'error');
      throw error;
    }
  }
}

// Export singleton instance
export const communicationService = new EnhancedCommunicationService();
export default communicationService;